import type { StoryblokAsset } from "@/types/storyblok";

/**
 * Transforms a Storyblok image URL to use modern formats with optimized compression
 * @param image - The Storyblok asset object
 * @param transformations - Optional image transformations (width, height, quality, etc.)
 * @returns The transformed image URL with optimized format
 */
export function getStoryblokImageUrl(
  image: StoryblokAsset | null | undefined,
  transformations?: {
    width?: number;
    height?: number;
    quality?: number;
    fit?: "in" | "out" | "fill" | "crop" | "scale";
    format?: "webp" | "jpg" | "png" | "auto";
    blur?: number;
    brightness?: number;
    contrast?: number;
    saturation?: number;
    hue?: number;
  }
): string {
  if (!image?.filename) {
    return "";
  }

  const baseUrl = image.filename;

  // If it's already a Storyblok CDN URL, we can transform it
  if (baseUrl.includes("a.storyblok.com")) {
    const params = new URLSearchParams();

    // Default to WebP format for better compression
    params.set("format", transformations?.format || "webp");

    // Add other transformations if provided
    if (transformations?.width) {
      params.set("width", transformations.width.toString());
    }
    if (transformations?.height) {
      params.set("height", transformations.height.toString());
    }
    if (transformations?.quality) {
      params.set("quality", transformations.quality.toString());
    }
    if (transformations?.fit) {
      params.set("fit", transformations.fit);
    }
    if (transformations?.blur) {
      params.set("blur", transformations.blur.toString());
    }
    if (transformations?.brightness) {
      params.set("brightness", transformations.brightness.toString());
    }
    if (transformations?.contrast) {
      params.set("contrast", transformations.contrast.toString());
    }
    if (transformations?.saturation) {
      params.set("saturation", transformations.saturation.toString());
    }
    if (transformations?.hue) {
      params.set("hue", transformations.hue.toString());
    }

    return `${baseUrl}?${params.toString()}`;
  }

  // If it's not a Storyblok CDN URL, return as-is
  return baseUrl;
}

/**
 * Gets a responsive image URL with multiple sizes for different screen sizes
 * @param image - The Storyblok asset object
 * @param sizes - Array of size configurations for different breakpoints
 * @returns Object with srcSet and sizes for responsive images
 */
export function getResponsiveImageUrl(
  image: StoryblokAsset | null | undefined,
  sizes: Array<{
    width: number;
    height?: number;
    quality?: number;
    fit?: "in" | "out" | "fill" | "crop" | "scale";
  }>
): { src: string; srcSet: string; sizes: string } {
  if (!image?.filename) {
    return { src: "", srcSet: "", sizes: "" };
  }

  const baseUrl = image.filename;

  if (!baseUrl.includes("a.storyblok.com")) {
    return { src: baseUrl, srcSet: "", sizes: "" };
  }

  // Generate srcSet with different sizes
  const srcSetParts = sizes.map((size) => {
    const params = new URLSearchParams();
    params.set("format", "webp");
    params.set("width", size.width.toString());

    if (size.height) {
      params.set("height", size.height.toString());
    }
    if (size.quality) {
      params.set("quality", size.quality.toString());
    }
    if (size.fit) {
      params.set("fit", size.fit);
    }

    return `${baseUrl}?${params.toString()} ${size.width}w`;
  });

  // Generate sizes attribute
  const sizesAttr = sizes
    .map((size, index) => {
      if (index === sizes.length - 1) {
        return `${size.width}px`;
      }
      return `(max-width: ${size.width}px) ${size.width}px`;
    })
    .join(", ");

  return {
    src: `${baseUrl}?format=webp&width=${sizes[0]?.width || 800}`,
    srcSet: srcSetParts.join(", "),
    sizes: sizesAttr,
  };
}

/**
 * Common image size presets for different use cases
 */
export const IMAGE_PRESETS = {
  hero: [
    { width: 1920, height: 1080, quality: 80, fit: "crop" as const },
    { width: 1280, height: 720, quality: 80, fit: "crop" as const },
    { width: 768, height: 432, quality: 75, fit: "crop" as const },
    { width: 480, height: 270, quality: 70, fit: "crop" as const },
  ],
  card: [
    { width: 600, height: 400, quality: 80, fit: "crop" as const },
    { width: 400, height: 267, quality: 75, fit: "crop" as const },
    { width: 300, height: 200, quality: 70, fit: "crop" as const },
  ],
  avatar: [
    { width: 72, height: 72, quality: 80, fit: "crop" as const },
    { width: 48, height: 48, quality: 75, fit: "crop" as const },
    { width: 36, height: 36, quality: 70, fit: "crop" as const },
  ],
  thumbnail: [
    { width: 400, height: 300, quality: 80, fit: "crop" as const },
    { width: 300, height: 225, quality: 75, fit: "crop" as const },
    { width: 200, height: 150, quality: 70, fit: "crop" as const },
  ],
} as const;
